import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Prompt } from 'react-router-dom'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { AlertTriangle, ArrowLeft, Search } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import RejectDialog from '../changes/RejectDialog'
import InvestorInvoiceDialog from './InvestorInvoiceDialog'
import InvoiceDialog from '../invoices/InvoiceDialog'
import { useApplicationMutations } from '../hooks/useApplicationMutations'
import {
  applicationAffordances, applicationTotals, draftProblem, draftRequested, exceedsContractValue, investorWarnings, isDraftDirty, isReviewDirty, manualEditorRows, reviewOf, reviewProblem,
  toLineInputs, toManualInputs, toManualReviewInputs, toReviewInputs, visibleRows, type DraftAmounts, type DraftReview, type DraftReviews, type ManualRow, type RowFilter,
} from '../lib/applications'
import { buildTree } from '../lib/tree'
import { costApplicationProtocolUrl } from '../lib/download'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import { formatMoney, moneyLocale } from '../lib/money'
import { recordDetailState } from '../lib/records'
import type { CostApplicationDetail, CostApplicationRow, CostControlView } from '../types'
import ApplicationDetailSkeleton from './ApplicationDetailSkeleton'
import ApplicationDialog from './ApplicationDialog'
import ApplicationHeader from './ApplicationHeader'
import PendingSubmittalsAlert from '../components/PendingSubmittalsAlert'
import ApplicationRowsTable from './ApplicationRowsTable'
import InvestorRowsTable from './InvestorRowsTable'
import ApplicationSummaryCard from './ApplicationSummaryCard'

const NOT_FOUND = 'CostControl_error_application_not_found'
type Pending = 'submit' | 'certify' | 'uncertify' | 'delete' | 'leave' | null
const CONFIRM: Record<Exclude<Pending, null>, { title: string; text: string; label: string }> = {
  submit: { title: 'CostControl_application_submit_title', text: 'CostControl_application_submit_text', label: 'CostControl_application_submit' },
  certify: { title: 'CostControl_application_certify_title', text: 'CostControl_application_certify_text', label: 'CostControl_application_certify' },
  uncertify: { title: 'CostControl_application_uncertify_title', text: 'CostControl_application_uncertify_text', label: 'CostControl_application_uncertify' },
  delete: { title: 'CostControl_application_delete_title', text: 'CostControl_application_delete_text', label: 'Common_delete' },
  leave: { title: 'CostControl_application_leave_title', text: 'CostControl_application_leave_text', label: 'CostControl_application_leave' },
}

const ApplicationDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { match, history } = useRouter()
  const { projectId, applicationId } = match.params as { projectId: string; applicationId: string }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const { data, loading, error } = useQuery<{ costApplication: CostApplicationDetail }>(Query.COST_APPLICATION, {
    variables: { id: applicationId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const currency = useCostCurrency(projectId)
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isRecordGoneError(message, NOT_FOUND)) setLost({ id, message })
  }, [])
  const detail = data?.costApplication
  const mutations = useApplicationMutations(projectId, detail?.application.contractId ?? '', onDetailError)
  const [draft, setDraft] = useState<DraftAmounts>({})
  const [review, setReview] = useState<DraftReviews>({})
  // Stable, so a row of the virtualized investor table re-renders only when its own entry changes.
  const onDraft = useCallback((key: string, value: string | null, valid: boolean) => setDraft((d) => ({ ...d, [key]: { value, valid } })), [])
  const onReview = useCallback((row: CostApplicationRow, patch: Partial<DraftReview>) => setReview((rv) => ({ ...rv, [row.key]: { ...reviewOf(row, rv), ...patch } })), [])
  /** The manual rows being edited; null while untouched (the stored ones apply). */
  const [manual, setManual] = useState<ManualRow[] | null>(null)
  const [filter, setFilter] = useState<RowFilter>({ onlyBilled: false, search: '' })
  const [editing, setEditing] = useState(false)
  const [returning, setReturning] = useState(false)
  const [invoicing, setInvoicing] = useState(false)
  const [recording, setRecording] = useState(false)
  const [removingInvoice, setRemovingInvoice] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const message = (lost && lost.id === applicationId ? lost.message : null) ?? error?.message ?? null
  const state = recordDetailState({
    loading, hasData: !!detail, recordProjectId: detail?.application.projectId ?? null, projectId, message,
    rightsLoading: actions.loading, canView: actions.canView, notFoundKey: NOT_FOUND,
  })
  const app = state === 'ready' && detail ? detail.application : null
  const af = applicationAffordances(actions, app, !!detail?.latest, detail?.access, detail?.contractKind)
  // A step moves the application to another state: edits of the old state no longer apply.
  useEffect(() => { setDraft({}); setReview({}); setManual(null) }, [app?.status])
  useEffect(() => { if (!af.editDraft) setEditing(false) }, [af.editDraft])
  useEffect(() => { if (!af.returnIt) setReturning(false) }, [af.returnIt])
  useEffect(() => { if (!af.invoice) setInvoicing(false) }, [af.invoice])
  useEffect(() => { if (!af.investorInvoice) setRecording(false) }, [af.investorInvoice])
  const allRows = useMemo(() => detail?.rows ?? [], [detail])
  // Manual rows never travel in the regular lines; the editor owns them in draft and review (Brema spec §3.4).
  const rows = useMemo(() => allRows.filter((r) => r.kind !== 'MANUAL'), [allRows])
  const storedManual = useMemo(() => allRows.filter((r) => r.kind === 'MANUAL'), [allRows])
  const mode = af.editDraft ? 'draft' : af.review ? 'review' : 'view'
  const editorManual = useMemo(() => manual ?? manualEditorRows(storedManual, mode === 'review'), [manual, storedManual, mode])
  const allowedNodeIds = useMemo(() => detail?.pairableNodeIds ?? [], [detail])
  const dirty = manual !== null || (mode === 'draft' ? isDraftDirty(rows, draft) : mode === 'review' ? isReviewDirty(rows, review) : false)
  const retentionPercent = detail?.retentionPercent ?? null
  const status = app?.status
  const totals = useMemo(() => {
    const regular = (list: typeof rows) => list.map((r) => {
      const requested = draftRequested(r, draft) ?? '0.00'
      const certified = status === 'DRAFT' ? requested : mode === 'review' ? reviewOf(r, review).certified ?? '0.00' : r.certified ?? requested
      return { requested, certified }
    })
    const edited = manual?.map((m) => ({ requested: mode === 'review' ? m.requested : m.amount ?? '0.00', certified: m.amount ?? '0.00' }))
    return applicationTotals([...regular(rows), ...(edited ?? regular(storedManual))], retentionPercent)
  }, [rows, storedManual, manual, draft, review, mode, status, retentionPercent])
  // Only while drafting or reviewing, and only when the server sent a check (null for a representative and on MAIN).
  const warnings = useMemo(() => {
    if (mode === 'view') return investorWarnings([], () => '0.00', [], null)
    const amountOf = (r: CostApplicationRow) => (mode === 'review' ? reviewOf(r, review).certified : draftRequested(r, draft)) ?? '0.00'
    return investorWarnings(rows, amountOf, editorManual, detail?.investorCheck ?? null)
  }, [mode, rows, review, draft, editorManual, detail])
  const skipPrompt = useRef(false)
  const leaveMessage = `${t(CONFIRM.leave.title)}\n${t(CONFIRM.leave.text)}`
  const back = () => {
    skipPrompt.current = true
    history.push(app ? `/project/${projectId}/cost-control/contracts/${app.contractId}` : `/project/${projectId}/cost-control?tab=contracts`)
  }

  if (state === 'loading') return <ApplicationDetailSkeleton />
  if (state !== 'ready' || !detail || !app) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t('CostControl_application_back')}
        </Button>
        <p className="text-sm text-muted-foreground">{state === 'error' ? describeCostControlError(message, t) : t(NOT_FOUND)}</p>
      </div>
    )
  }

  // Spec §2.3: the investor application has its own grouped, virtualized table with its own search and filter.
  const investor = detail.contractKind === 'MAIN'
  const RowsTable = investor ? InvestorRowsTable : ApplicationRowsTable
  const listed = mode === 'view' ? allRows : rows
  const shown = investor ? listed : visibleRows(listed, filter, (r) => draftRequested(r, draft))
  const showReview = (app.status !== 'DRAFT' && !(detail.access === 'REPRESENTATIVE' && app.status === 'SUBMITTED')) || allRows.some((r) => r.certified !== null || !!r.note)
  const hasContractorManual = storedManual.some((r) => r.requested !== '0.00')
  // Brema spec §3.3: manual rows may outweigh the work, but an application totalling 0 or less has nothing to invoice.
  const totalBlocker = (amount: string) => (Number(amount) > 0 ? null : 'CostControl_error_application_total_positive')
  const submitBlocker = dirty ? 'CostControl_application_save_first' : draftProblem(rows, {}) ?? (toLineInputs(rows, {}).length || hasContractorManual ? totalBlocker(totals.requested) : 'CostControl_error_application_no_lines')
  const certifyBlocker = reviewProblem(rows, review, manual) ?? totalBlocker(totals.certified)
  const saveProblem = mode === 'draft' ? draftProblem(rows, draft, manual) : mode === 'review' ? reviewProblem(rows, review, manual) : null

  const save = async () => {
    if (saveProblem) {
      toastr.error(t('Common_error'), t(saveProblem))
      return
    }
    const res = mode === 'draft'
      ? await mutations.setLines(app.id, toLineInputs(rows, draft), manual ? toManualInputs(manual) : undefined)
      : await mutations.saveReview(app.id, toReviewInputs(rows, review), manual ? toManualReviewInputs(manual) : undefined)
    // The editor re-derives from the refetched rows, so new rows now carry their server keys. Without that reload the
    // stored rows are stale: the edit stays, and saving it again replaces the manual rows with exactly what it shows.
    if (res.ok && res.reloaded) setManual(null)
  }
  const confirm = async () => {
    if (!pending) return
    if (pending === 'leave') {
      setPending(null)
      back()
      return
    }
    if (pending === 'delete') {
      const res = await mutations.remove(app.id)
      setPending(null)
      if (res.ok) {
        toastr.success('', t('CostControl_application_deleted'))
        back()
      }
      return
    }
    if (pending === 'submit') await mutations.submit(app.id)
    if (pending === 'certify') {
      // Certify takes no manual rows: edited ones are saved with the review first.
      if (manual) {
        const saved = await mutations.saveReview(app.id, toReviewInputs(rows, review), toManualReviewInputs(manual))
        if (!saved.ok) { setPending(null); return }
        await mutations.certify(app.id, null)
      } else await mutations.certify(app.id, isReviewDirty(rows, review) ? toReviewInputs(rows, review) : null)
    }
    if (pending === 'uncertify') await mutations.uncertify(app.id)
    setPending(null)
  }
  const confirmText = pending ? (pending === 'certify' && investor ? { ...CONFIRM.certify, text: 'CostControl_application_certify_text_main' } : CONFIRM[pending]) : null

  return (
    <div className="space-y-4">
      <Prompt when={dirty} message={() => (skipPrompt.current ? true : leaveMessage)} />
      <ApplicationHeader
        application={app}
        af={af}
        dirty={dirty}
        saving={mutations.saving}
        submitBlocker={submitBlocker}
        certifyBlocker={certifyBlocker}
        onBack={() => (dirty ? setPending('leave') : back())}
        onEdit={() => setEditing(true)}
        onSave={save}
        onSubmit={() => setPending('submit')}
        onReturn={() => setReturning(true)}
        onCertify={() => setPending('certify')}
        onUncertify={() => setPending('uncertify')}
        onInvoice={() => setInvoicing(true)}
        onInvestorInvoice={() => setRecording(true)}
        removingInvoice={removingInvoice}
        onRemoveInvestorInvoice={async () => { setRemovingInvoice(true); try { await mutations.setInvestorInvoice(app.id, null) } finally { setRemovingInvoice(false) } }}
        onDelete={() => setPending('delete')}
        onProtocol={() => { window.open(costApplicationProtocolUrl(app.id), '_blank') }}
      />
      {app.status === 'SUBMITTED' && <PendingSubmittalsAlert projectId={projectId} submittals={detail.pendingSubmittals} />}
      <ApplicationSummaryCard application={app} projectId={projectId} />
      {exceedsContractValue(rows, totals.certified, detail.contractValue) && (
        <p className="text-sm text-destructive">{t('CostControl_application_over_contract', { value: formatMoney(detail.contractValue, { locale, currency }) })}</p>
      )}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">{t('CostControl_application_rows_title')}</CardTitle>
          {!investor && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch id="cc-app-billed" checked={filter.onlyBilled} onCheckedChange={(onlyBilled) => setFilter((f) => ({ ...f, onlyBilled }))} />
                <Label htmlFor="cc-app-billed" className="text-sm">{t('CostControl_application_only_billed')}</Label>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder={t('Common_search')} value={filter.search} onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))} className="h-8 w-[200px] pl-8 text-sm" />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {warnings.size > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t('CostControl_billing_review_alert', { count: warnings.size })}</AlertDescription>
            </Alert>
          )}
          <RowsTable
            rows={shown}
            hasRows={allRows.length > 0}
            mode={mode}
            status={app.status}
            showReview={showReview}
            draft={draft}
            review={review}
            totals={totals}
            retentionPercent={detail.retentionPercent}
            disabled={mutations.saving}
            currency={currency}
            locale={locale}
            onDraft={onDraft}
            onReview={onReview}
            manual={editorManual}
            allowedNodeIds={allowedNodeIds}
            tree={tree}
            onManual={setManual}
            investorWarnings={warnings}
          />
        </CardContent>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordAttachmentsCard
          projectId={projectId}
          attachments={detail.attachments}
          canAdd={af.addAttachment}
          canRemove={af.removeAttachment}
          canLinkDocuments={docControl.canView}
          onAdd={(source) => mutations.addAttachment(app.id, source)}
          onRemove={(attachmentId) => mutations.removeAttachment(app.id, attachmentId)}
        />
        <RecordHistoryCard projectId={projectId} entityType="APPLICATION" entityId={app.id} currency={currency} locale={locale} />
      </div>
      <ApplicationDialog open={editing} mode="edit" application={app} onOpenChange={setEditing} onSubmit={async (input) => (await mutations.update(app.id, input)).ok} />
      <RejectDialog
        open={returning}
        onOpenChange={setReturning}
        titleKey="CostControl_application_return_title"
        hintKey="CostControl_application_return_hint"
        noteKey="CostControl_application_return_note"
        actionKey="CostControl_application_return"
        onReject={async (note) => (await mutations.returnIt(app.id, note)).ok}
      />
      <InvoiceDialog
        open={invoicing}
        mode="fromApplication"
        initialRetentionPercent={detail.retentionPercent}
        currency={currency}
        onOpenChange={setInvoicing}
        onSubmit={async (input) => {
          const res = await mutations.invoice(app.id, input)
          if (res.ok) history.push(`/project/${projectId}/cost-control/invoices/${res.data.createCostInvoiceFromApplication.id}`)
          return res.ok
        }}
      />
      <InvestorInvoiceDialog
        open={recording}
        invoice={app.investorInvoice}
        onOpenChange={setRecording}
        onSubmit={async (input) => (await mutations.setInvestorInvoice(app.id, input)).ok}
      />
      <ConfirmDialog
        open={!!pending}
        title={confirmText ? t(confirmText.title) : ''}
        description={confirmText ? t(confirmText.text) : undefined}
        confirmLabel={confirmText ? t(confirmText.label) : ''}
        variant={pending === 'delete' || pending === 'leave' ? 'destructive' : 'default'}
        submitting={mutations.saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  )
}
export default ApplicationDetail
